import { withLegacyCompat } from '@adornis/buchungsheld-components/legacy-editor-compat.js';
import { TextMultipleLangItem } from '@adornis/buchungsheld-db/entities/TextMultipleLangItem.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import type { FunctionalFormFieldProps } from '@adornis/formfield/functional-form-field.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { HtmlEditor } from '@adornis/html-editor/client/HtmlEditor.js';
import { richTextEditorConfig } from '@adornis/prosemirror-editor/presets/rich-text-editor.js';
import type { IMarkConfig } from '@adornis/prosemirror-editor/types.js';
import { Language } from '@adornis/translation-core/languages.js';
import { html } from 'lit';
import { keyed } from 'lit/directives/keyed.js';

export const MultilanguageFormattableTextarea = stateful((props: FunctionalFormFieldProps<TextMultipleLangItem[]>) => {
  const design = useObservable(() => DesignSystem.currentTheme, []);
  const [selectedLang, setSelectedLang] = useState<Language>(Language.GERMAN, []);
  const placeholder =
    !props.placeholder || props.placeholder.endsWith('_placeholder') ? 'Hier tippen...' : props.placeholder;

  return html`
    <div class="relative w-full">
      <div class="relative flex flex-row gap-2">
        <div class="flex flex-col items-center gap-2 pr-2 border-r border-base-300 select-none">
          <button
            type="button"
            class="btn btn-ghost btn-xs ${selectedLang === Language.GERMAN ? 'btn-active' : ''}"
            @click=${() => setSelectedLang(Language.GERMAN)}
          >
            DE
          </button>
          <button
            type="button"
            class="btn btn-ghost btn-xs ${selectedLang === Language.ENGLISH ? 'btn-active' : ''}"
            @click=${() => setSelectedLang(Language.ENGLISH)}
          >
            EN
          </button>
        </div>
        <div
          class="flex-1 w-full px-3 py-2 bg-base-200 border ${
            props.error ? 'border-error bg-error/20' : 'border-base-300'
          } rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50 ${
            props.disabled ? 'opacity-50 cursor-not-allowed' : ''
          } resize-y"
        >
          ${keyed(
            selectedLang,
            HtmlEditor({
              configs: [
                ...withLegacyCompat(
                  richTextEditorConfig({
                    colors: design
                      ? [design.colors.tone.error, design.colors.tone.warning, design.colors.tone.success]
                          .filter(color => color !== undefined)
                          .map(color => color.toString())
                      : [],
                  }),
                ),
                itemList(),
              ],
              placeholder,
              value: props.value?.find(v => v.langKey === selectedLang)?.text ?? '',
              disabled: props.disabled,
              onPicked: value => {
                if (!props.value) props.value = [];
                const v = value ?? '';

                let selectedLanguageItem = props.value.find(v => v.langKey === selectedLang);
                if (!selectedLanguageItem) {
                  selectedLanguageItem = new TextMultipleLangItem({ langKey: selectedLang, text: v });
                  props.value.push(selectedLanguageItem);
                } else {
                  selectedLanguageItem.text = v;
                }

                props.onPicked?.(props.value);
              },
            }),
          )}
        </div>
      </div>
      ${props.error ? html`<p class="text-xs text-error mt-1">${props.error}</p>` : ''}
    </div>
  `;
});

export enum MenuItemGroup {
  TEXT_STYLE = 'text-style',
  TEXT_ALIGNMENT = 'text-aignment',
  HEADINGS = 'headings',
  COLOR = 'color',
  LAYOUT = 'layout',
  MEDIA = 'media',
  BASIC = 'basic',
  SETTINGS = 'settings',
}

function itemList(): IMarkConfig {
  const LIST_KEY = 'itemList2';

  return {
    mark: { name: LIST_KEY, schema: {} },
    menuItems: [
      {
        group: MenuItemGroup.TEXT_STYLE,
        label: 'Bullet List',
        icon: 'icon-[mdi--format-list-bulleted]',
        shouldVisualize: view => {
          return true;
        },
        run: async view => {
          const { state } = view;
          const { tr } = view.state;
          const nodeType = state.schema.nodes.list;
          if (!nodeType) return false;

          const selectionFrom = view.state.selection.from;
          const resolvedPos = view.state.doc.resolve(selectionFrom);
          const currentNode = resolvedPos.parent; // parent gibt die Node an der Position zurück
          const listItemContent = state.schema.nodes.paragraph!.create({}, [state.schema.text('listitem 1')]);
          const listItem = nodeType.create({}, [listItemContent]);

          if (currentNode && currentNode.type === state.schema.nodes.paragraph && currentNode.textContent === '') {
            // Ersetze den leeren Paragraphen durch den neuen Node
            tr.replaceSelectionWith(listItem);
          } else {
            // Andernfalls füge den Node wie gewohnt ein
            tr.insert(selectionFrom, listItem);
          }

          view.dispatch(tr);
          return true;
        },
      },
    ],
    plugins: schema => [],
  };
}
